/**
 * Regras de auto-mapping de colunas — PT + EN.
 * Aliases sao pre-normalizados com normalizeColumnKey (lower, sem acento, sem separadores).
 */
import { normalizeColumnKey } from "./normalizers";

export type DbField = { key: string; label: string };

/** Campos do paciente disponiveis na importacao. "name" e obrigatorio. */
export const PATIENT_DB_FIELDS: DbField[] = [
  { key: "name", label: "Nome" },
  { key: "email", label: "E-mail" },
  { key: "phone", label: "Celular" },
  { key: "landline", label: "Telefone fixo" },
  { key: "birthDate", label: "Data de nascimento" },
  { key: "document", label: "CPF" },
  { key: "identifier", label: "RG / Documento" },
  { key: "sex", label: "Sexo" },
  { key: "address", label: "Endereco" },
  { key: "addressNumber", label: "Numero" },
  { key: "addressComplement", label: "Complemento" },
  { key: "neighborhood", label: "Bairro" },
  { key: "city", label: "Cidade" },
  { key: "state", label: "UF" },
  { key: "zipCode", label: "CEP" },
  { key: "profession", label: "Profissao" },
  { key: "clinicalRecordNumber", label: "N. prontuario" },
  { key: "howDidMeet", label: "Como conheceu" },
  { key: "insurancePlanName", label: "Convenio" },
  { key: "insurancePlanNumber", label: "N. convenio" },
  { key: "motherName", label: "Nome da mae" },
  { key: "notes", label: "Observacoes" },
  { key: "active", label: "Ativo" },
];

const PATIENT_ALIASES: Record<string, string[]> = {
  name: ["name", "nome", "paciente", "patient", "fullname", "full_name", "nomecompleto", "nome completo", "nomepaciente"],
  email: ["email", "e-mail", "emailaddress", "enderecoemail"],
  phone: ["phone", "celular", "cellphone", "mobile", "mobilephone", "telefonecelular", "cel", "whatsapp", "fonecelular"],
  landline: ["landline", "telefone", "telephone", "telefonefixo", "fone", "tel", "telefoneresidencial", "foneresidencial"],
  birthDate: ["birth_date", "birthdate", "datanascimento", "data_nascimento", "nascimento", "dob", "dateofbirth", "dt_nascimento", "dtnascimento", "datanasc"],
  document: ["cpf", "cpf_cnpj", "documento fiscal"],
  identifier: ["identifier", "rg", "documento", "document", "identity", "identidade"],
  sex: ["sex", "sexo", "gender", "genero"],
  address: ["address", "endereco", "enderecocompleto", "logradouro", "street", "rua"],
  addressNumber: ["numero", "num", "number"],
  addressComplement: ["complemento", "complement"],
  neighborhood: ["bairro", "neighborhood", "district"],
  city: ["cidade", "city", "municipio"],
  state: ["uf", "estado", "state"],
  zipCode: ["zip", "cep", "zipcode", "postalcode", "codigopostal"],
  profession: ["profession", "profissao", "ocupacao", "occupation", "job"],
  clinicalRecordNumber: ["clinical_record_number", "prontuario", "numeroprontuario", "record", "recordnumber", "numprontuario", "codpaciente", "codigo", "codigopaciente"],
  howDidMeet: ["how_did_meet", "comoconheceu", "indicacao", "referral", "howdidmeet", "indicadopor"],
  insurancePlanName: ["insurance_plan_name", "convenio", "plano", "insurance", "healthplan", "planosaude", "nomeconvenio"],
  insurancePlanNumber: ["insurance_plan_number", "carteirinha", "numeroconvenio", "insurancenumber", "numconvenio", "numcarteirinha", "carteira"],
  motherName: ["mother_name", "nomedamae", "mae", "mothername", "nomemae", "filiacao"],
  notes: ["notes", "observacoes", "obs", "observacao", "nota", "notas", "comments", "anotacoes"],
  active: ["is_active", "ativo", "active", "status", "situacao"],
};

/** Campos do appointment disponiveis na importacao. patientName e obrigatorio. */
export const APPOINTMENT_DB_FIELDS: DbField[] = [
  { key: "patientName", label: "Nome do paciente" },
  { key: "patientPhone", label: "Telefone do paciente" },
  { key: "date", label: "Data" },
  { key: "startTime", label: "Hora inicio" },
  { key: "endTime", label: "Hora fim" },
  { key: "procedure", label: "Procedimento" },
  { key: "status", label: "Status" },
  { key: "notes", label: "Observacoes" },
  { key: "dentistName", label: "Dentista" },
  { key: "cancelled", label: "Cancelado" },
];

const APPOINTMENT_ALIASES: Record<string, string[]> = {
  patientName: ["patientname", "patient", "paciente", "nome", "name", "nomepaciente"],
  patientPhone: ["mobilephone", "phone", "celular", "telefone", "mobile", "fone", "whatsapp"],
  date: ["date", "data", "dataagendamento", "dataconsulta", "appointmentdate"],
  startTime: ["fromtime", "starttime", "horainicio", "inicio", "start", "from"],
  endTime: ["totime", "endtime", "horafim", "fim", "end", "to"],
  procedure: ["procedures", "procedure", "procedimento", "tratamento", "treatment"],
  status: ["status", "situacao"],
  notes: ["notes", "observacoes", "obs", "nota", "comments"],
  dentistName: ["dentistname", "dentist", "dentista", "profissional", "doctor", "medico", "doutor"],
  cancelled: ["canceled", "cancelled", "cancelado", "cancelada"],
};

function buildAutoMap(
  headers: string[],
  aliases: Record<string, string[]>,
): Record<string, string> {
  const result: Record<string, string> = {};
  const used = new Set<string>();
  for (const header of headers) {
    const key = normalizeColumnKey(header);
    for (const [dbField, variants] of Object.entries(aliases)) {
      if (used.has(dbField)) continue;
      if (variants.some((v) => normalizeColumnKey(v) === key)) {
        result[header] = dbField;
        used.add(dbField);
        break;
      }
    }
  }
  return result;
}

export function autoMapPatientColumns(headers: string[]): Record<string, string> {
  return buildAutoMap(headers, PATIENT_ALIASES);
}

export function autoMapAppointmentColumns(headers: string[]): Record<string, string> {
  return buildAutoMap(headers, APPOINTMENT_ALIASES);
}
